import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useEffect } from "react";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { loadGraphemePinyinMap } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { getHanziScriptConvertersQueryOptions } from "@-/dev.improve.haniverse.lib.hanzi-script/query-options";

export function StaticDatabaseWarmer() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const queryClient = useQueryClient();

  // Preload the synchronous glyph → main-reading map (tone colors + fallback readings are
  // resolved during render and can't await). It reads the main pronunciation cards, so it lives
  // in the content DB. Until this resolves, lookups degrade to `null`.
  useEffect(() => {
    let cancelled = false;
    void loadGraphemePinyinMap(contentDb(null)).catch((error: unknown) => {
      if (!cancelled) {
        logger(import.meta.location).warn(
          "[content-db] failed to load grapheme main-reading map",
          error,
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [contentDb]);

  // Warm the simplified↔traditional script converters off the critical path. `opencc-js` ships
  // its conversion dictionaries (~1 MB) and is dynamically imported, so this fetches its chunk
  // ahead of the first cross-script glyph render; until it resolves, glyphs show the original
  // script unchanged.
  useEffect(() => {
    void queryClient.prefetchQuery(getHanziScriptConvertersQueryOptions());
  }, [queryClient]);

  // (The Taiwan MOE per-glyph usage map is no longer warmed here either: its consumers now read
  // the precomputed `taiwan-usage-map.gen.json` synchronously via `getTaiwanUsageMap`. That removed
  // the eager whole-`grapheme`-table scan this used to prefetch — ~2s of JS-thread time on a slow
  // device. Same change as the decomposition-components map below.)

  // (The grapheme → decomposition-components map is no longer warmed here: the only on-device
  // consumer was `useGetGraphemePrevalence`, which now reads the precomputed component-prevalence
  // closures synchronously — see `getComponentPrevalenceLookups`. That removed both this ~1s
  // startup query and the per-startup BFS over the whole decomposition graph.)

  return null;
}
